import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQueue } from "../contexts/QueueContext";
import { useToast } from "../contexts/ToastContext";
import { useDocumentStore } from "../store/documentState";
import { useAuth } from "../contexts/AuthContext";
import {
  AbschlussberichtAnswer,
  AbschlussberichtDraft,
  AbschlussberichtStoredSubmission,
  AbschlussberichtSubmission,
  AbschlussberichtSummaryQuantities,
  AbschlussberichtTemplate,
} from "../types/abschlussbericht";
import {
  getAbschlussberichtTemplate,
  upsertAbschlussberichtDraft,
} from "../services/api";
import { getEnv } from "../utils/env";

interface AbschlussberichtWizardProps {
  projectId: string;
  phaseId: string;
  disabled?: boolean;
  onCompleted?: (submittedAt: string) => void;
}

type AnswerMap = Record<string, AbschlussberichtAnswer>;
type ChecklistStateMap = Record<string, boolean[]>;
type DescriptionVisibilityMap = Record<string, boolean>;
type QuantityFieldKey = keyof AbschlussberichtSummaryQuantities;
type QuantityInputState = Record<QuantityFieldKey, string>;

interface AbschlussberichtLocalDraftState {
  answers: AnswerMap;
  checklistStates: ChecklistStateMap;
  descriptionVisibility: DescriptionVisibilityMap;
  currentStepIndex: number;
  confirmationAccepted: boolean;
  quantityInputs: QuantityInputState;
}

const EMPTY_QUANTITY_INPUTS: QuantityInputState = {
  ventileAnzahl: "",
  rohreInnen: "",
  rohreAussen: "",
};

type WarrantyPaymentChoice = NonNullable<AbschlussberichtAnswer["warrantyPaymentChoice"]>;

const WARRANTY_PAYMENT_OPTIONS: Array<{ value: WarrantyPaymentChoice; label: string }> = [
  { value: "monthly", label: "Kunde zahlt monatlich" },
  { value: "one_time", label: "Kunde zahlt einmalig" },
  { value: "sales_follow_up", label: "Vertriebler soll nachhaken" },
];

const getWarrantyPaymentChoiceLabel = (
  choice?: AbschlussberichtAnswer["warrantyPaymentChoice"]
): string | null =>
  WARRANTY_PAYMENT_OPTIONS.find((option) => option.value === choice)?.label || null;

const getChecklistItemsFromHtml = (html: string): string[] => {
  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
    return [];
  }

  const document = new window.DOMParser().parseFromString(html, "text/html");
  return Array.from(document.querySelectorAll("li"))
    .map((item) => item.textContent?.replace(/\s+/g, " ").trim() || "")
    .filter(Boolean);
};

const toAnswerMap = (answers: AbschlussberichtAnswer[]): AnswerMap =>
  answers.reduce<AnswerMap>((accumulator, answer) => {
    accumulator[answer.stepId] = answer;
    return accumulator;
  }, {});

const normalizeQuantityInputs = (
  quantities?: Partial<AbschlussberichtSummaryQuantities> | null
): QuantityInputState => ({
  ventileAnzahl:
    typeof quantities?.ventileAnzahl === "number" && quantities.ventileAnzahl >= 0
      ? String(quantities.ventileAnzahl)
      : "",
  rohreInnen:
    typeof quantities?.rohreInnen === "number" && quantities.rohreInnen >= 0
      ? String(quantities.rohreInnen)
      : "",
  rohreAussen:
    typeof quantities?.rohreAussen === "number" && quantities.rohreAussen >= 0
      ? String(quantities.rohreAussen)
      : "",
});

const getQuantityValue = (value: string): number =>
  /^\d+$/.test(value) ? Number(value) : 0;

const getSummaryQuantitiesFromInputs = (
  quantityInputs: QuantityInputState
): AbschlussberichtSummaryQuantities => ({
  ventileAnzahl: getQuantityValue(quantityInputs.ventileAnzahl),
  rohreInnen: getQuantityValue(quantityInputs.rohreInnen),
  rohreAussen: getQuantityValue(quantityInputs.rohreAussen),
});

const normalizeChecklistStates = (
  checklistStates: ChecklistStateMap
): ChecklistStateMap =>
  Object.fromEntries(
    Object.entries(checklistStates).map(([stepId, values]) => [
      stepId,
      (values || []).map((value) => value === true),
    ])
  );

const buildSourceSignature = (template: AbschlussberichtTemplate): string =>
  JSON.stringify({
    projectId: template.projectId,
    sourceDocumentId: template.sourceDocumentId,
    sourceDocumentNumber: template.sourceDocumentNumber,
    templateKey: template.templateKey,
    templateVersion: template.templateVersion,
    steps: template.steps.map((step) => ({
      id: step.id,
      itemNumber: step.itemNumber,
      quantityLabel: step.quantityLabel,
      displayTitle: step.displayTitle,
      checklistHtml: step.checklistHtml,
      descriptionHtml: step.descriptionHtml,
    })),
  });

const clampStepIndex = (value: number, max: number) => Math.max(0, Math.min(value, max));

const SHOW_POSITION_CHECKLIST =
  getEnv("VITE_ENABLE_ABSCHLUSSBERICHT_POSITION_CHECKLIST") === "true";

const PROJECT_COMPLETION_STEP: AbschlussberichtTemplate["steps"][number] = {
  id: "project-completion",
  itemNumber: "Abschluss",
  groupTitle: "Projekt Abschluss",
  displayTitle: "Projekt Abschluss",
  descriptionHtml:
    "<p>Das Projekt ist vollständig abgeschlossen.</p><ul><li>Ich habe die Baustelle vollständig abgeschlossen.</li><li>Es fallen keine Nacharbeiten an.</li><li>Das Projekt kann abgerechnet werden.</li></ul>",
  checklistHtml: "",
};

const isProjectCompletionStep = (stepId: string) => stepId === PROJECT_COMPLETION_STEP.id;

const normalizeStepSearchText = (value: string): string =>
  value
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

type AbschlussberichtStep = AbschlussberichtTemplate["steps"][number];

const getWarrantyStepSearchText = (step: AbschlussberichtStep) => {
  const groupText = normalizeStepSearchText(step.groupTitle || "");
  const stepText = normalizeStepSearchText(
    [
      step.displayTitle,
      step.descriptionHtml,
      step.checklistHtml,
    ]
      .filter(Boolean)
      .join(" ")
  );

  return { groupText, stepText };
};

const isWarrantyGroupStep = (step: AbschlussberichtStep): boolean => {
  const { groupText } = getWarrantyStepSearchText(step);
  return groupText.includes("garantie") && groupText.includes("wartung");
};

const isYearlyWarrantyPaymentStep = (step: AbschlussberichtStep): boolean => {
  const { groupText, stepText } = getWarrantyStepSearchText(step);
  const hasWarrantyContext =
    groupText.includes("garantie") ||
    groupText.includes("wartung") ||
    stepText.includes("garantie") ||
    stepText.includes("wartung");

  return hasWarrantyContext && stepText.includes("jahr");
};

const getWarrantyPaymentStepId = (steps: AbschlussberichtStep[]): string | null =>
  (steps.find(isYearlyWarrantyPaymentStep) || steps.find(isWarrantyGroupStep))?.id || null;

const AbschlussberichtWizard: React.FC<AbschlussberichtWizardProps> = ({
  projectId,
  phaseId,
  disabled = false,
  onCompleted,
}) => {
  const { addToQueue, items } = useQueue();
  const { showToast } = useToast();
  const { user } = useAuth();
  const getPhaseStatus = useDocumentStore((state) => state.getPhaseStatus);
  const updatePhaseStatus = useDocumentStore((state) => state.updatePhaseStatus);

  const [template, setTemplate] = useState<AbschlussberichtTemplate | null>(null);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [localSubmission, setLocalSubmission] = useState<AbschlussberichtSubmission | null>(null);
  const [isRestartedForEditing, setIsRestartedForEditing] = useState(false);
  const [confirmationAccepted, setConfirmationAccepted] = useState(false);
  const [checklistStates, setChecklistStates] = useState<ChecklistStateMap>({});
  const [descriptionVisibility, setDescriptionVisibility] = useState<DescriptionVisibilityMap>({});
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [quantityInputs, setQuantityInputs] = useState<QuantityInputState>(EMPTY_QUANTITY_INPUTS);
  const autosaveRef = useRef<number | null>(null);

  const steps = useMemo(
    () => (template ? [...template.steps, PROJECT_COMPLETION_STEP] : []),
    [template]
  );
  const warrantyPaymentStepId = useMemo(
    () => (template ? getWarrantyPaymentStepId(template.steps) : null),
    [template]
  );
  const quantitiesStepIndex = steps.length;
  const summaryStepIndex = steps.length + 1;
  const queueSubmission = useMemo(() => {
    const queueItem = [...items]
      .reverse()
      .find(
        (item) =>
          item.type === "abschlussbericht_submit" &&
          item.data?.projectId === projectId &&
          item.data?.phaseId === phaseId
      );

    return (queueItem?.data?.submission || null) as AbschlussberichtSubmission | null;
  }, [items, phaseId, projectId]);

  const readOnlySubmission = useMemo<AbschlussberichtStoredSubmission | AbschlussberichtSubmission | null>(() => {
    if (isRestartedForEditing) {
      return null;
    }
    return template?.existingSubmission || queueSubmission || localSubmission;
  }, [isRestartedForEditing, localSubmission, queueSubmission, template?.existingSubmission]);

  const draftStorageKey = useMemo(
    () =>
      template
        ? `abschlussbericht-wizard-draft:${projectId}:${phaseId}:${template.templateKey}:${template.templateVersion}:${template.sourceDocumentId}`
        : null,
    [phaseId, projectId, template]
  );

  const draftQueueKey = useMemo(
    () =>
      template
        ? `abschlussbericht-draft:${projectId}:${template.templateVersion}:${template.sourceDocumentId}`
        : null,
    [projectId, template]
  );

  const isQuantitiesStep = currentStepIndex === quantitiesStepIndex;
  const isSummaryStep = currentStepIndex === summaryStepIndex;
  const currentStep = !isQuantitiesStep && !isSummaryStep ? steps[currentStepIndex] : null;
  const currentAnswer = currentStep ? answers[currentStep.id] : null;
  const currentStepRequiresWarrantyPaymentChoice = currentStep
    ? currentStep.id === warrantyPaymentStepId
    : false;
  const currentChecklistHtml = currentStep?.checklistHtml?.trim()
    ? currentStep.checklistHtml
    : currentStep?.descriptionHtml || "";
  const currentChecklistItems = useMemo(
    () => (currentChecklistHtml ? getChecklistItemsFromHtml(currentChecklistHtml) : []),
    [currentChecklistHtml]
  );
  const currentChecklistState = currentStep ? checklistStates[currentStep.id] || [] : [];
  const isCurrentDescriptionExpanded = currentStep
    ? isProjectCompletionStep(currentStep.id) || !!descriptionVisibility[currentStep.id]
    : false;
  const areAllCurrentChecklistItemsChecked =
    currentChecklistItems.length > 0 &&
    currentChecklistItems.every((_, index) => !!currentChecklistState[index]);
  const summaryQuantities = useMemo(
    () => getSummaryQuantitiesFromInputs(quantityInputs),
    [quantityInputs]
  );
  const hasValidSummaryQuantities =
    quantityInputs.ventileAnzahl !== "" &&
    quantityInputs.rohreInnen !== "" &&
    quantityInputs.rohreAussen !== "" &&
    summaryQuantities.ventileAnzahl >= 0 &&
    summaryQuantities.rohreInnen >= 0 &&
    summaryQuantities.rohreAussen >= 0;

  useEffect(() => {
    let isMounted = true;

    const loadTemplate = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getAbschlussberichtTemplate(projectId);
        if (!isMounted) {
          return;
        }

        setTemplate(response);
        setAnswers({});
        setChecklistStates({});
        setDescriptionVisibility({});
        setCurrentStepIndex(0);
        setConfirmationAccepted(false);
        setDraftHydrated(false);
        setQuantityInputs(EMPTY_QUANTITY_INPUTS);

        if (response.existingSubmission) {
          updatePhaseStatus(
            phaseId,
            "completed",
            response.existingSubmission.submittedAt,
            response.existingSubmission.submittedByEmail
          );
          onCompleted?.(response.existingSubmission.submittedAt);
        } else if (!queueSubmission) {
          updatePhaseStatus(phaseId, "pending");
        }
      } catch (loadError) {
        const message =
          loadError instanceof Error
            ? loadError.message
            : "Abschlussbericht konnte nicht geladen werden";
        if (isMounted) {
          setError(message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadTemplate();
    return () => {
      isMounted = false;
    };
  }, [phaseId, projectId]);

  useEffect(() => {
    if (queueSubmission) {
      setLocalSubmission(queueSubmission);
    }
  }, [queueSubmission]);

  useEffect(() => {
    if (!template) {
      return;
    }

    if (template.existingSubmission) {
      setAnswers(toAnswerMap(template.existingSubmission.answers));
      setQuantityInputs(normalizeQuantityInputs(template.existingSubmission.summaryQuantities));
      setChecklistStates({});
      setDescriptionVisibility({});
      setCurrentStepIndex(summaryStepIndex);
      setConfirmationAccepted(true);
      setDraftHydrated(true);
      return;
    }

    if (template.existingDraft) {
      setAnswers(toAnswerMap(template.existingDraft.answers));
      setQuantityInputs(normalizeQuantityInputs(template.existingDraft.summaryQuantities));
      setChecklistStates(template.existingDraft.checklistStates || {});
      setDescriptionVisibility({});
      setCurrentStepIndex(clampStepIndex(template.existingDraft.currentStepIndex, summaryStepIndex));
      setConfirmationAccepted(false);
      setDraftHydrated(true);
      return;
    }

    if (!draftStorageKey || queueSubmission || localSubmission) {
      setDraftHydrated(true);
      return;
    }

    try {
      const rawDraft = window.localStorage.getItem(draftStorageKey);
      if (!rawDraft) {
        setDraftHydrated(true);
        return;
      }

      const draft = JSON.parse(rawDraft) as Partial<AbschlussberichtLocalDraftState>;
      setAnswers(draft.answers || {});
      setChecklistStates(draft.checklistStates || {});
      setDescriptionVisibility(draft.descriptionVisibility || {});
      setCurrentStepIndex(
        clampStepIndex(
          typeof draft.currentStepIndex === "number" ? draft.currentStepIndex : 0,
          summaryStepIndex
        )
      );
      setConfirmationAccepted(!!draft.confirmationAccepted);
      setQuantityInputs(draft.quantityInputs || EMPTY_QUANTITY_INPUTS);
    } catch {
      window.localStorage.removeItem(draftStorageKey);
    } finally {
      setDraftHydrated(true);
    }
  }, [draftStorageKey, localSubmission, queueSubmission, summaryStepIndex, template]);

  useEffect(() => {
    if (!draftStorageKey || !draftHydrated) {
      return;
    }

    if (readOnlySubmission) {
      window.localStorage.removeItem(draftStorageKey);
      return;
    }

    const draft: AbschlussberichtLocalDraftState = {
      answers,
      checklistStates,
      descriptionVisibility,
      currentStepIndex,
      confirmationAccepted,
      quantityInputs,
    };

    window.localStorage.setItem(draftStorageKey, JSON.stringify(draft));
  }, [
    answers,
    checklistStates,
    confirmationAccepted,
    currentStepIndex,
    descriptionVisibility,
    draftHydrated,
    draftStorageKey,
    quantityInputs,
    readOnlySubmission,
  ]);

  useEffect(() => {
    if (!currentStep || !areAllCurrentChecklistItemsChecked) {
      return;
    }

    setAnswers((previous) => {
      const existing = previous[currentStep.id];
      if (existing?.planned) {
        return previous;
      }

      return {
        ...previous,
        [currentStep.id]: {
          stepId: currentStep.id,
          itemNumber: currentStep.itemNumber,
          displayTitle: currentStep.displayTitle,
          planned: true,
        },
      };
    });
  }, [areAllCurrentChecklistItemsChecked, currentStep]);

  useEffect(() => {
    if (!template || !draftHydrated || readOnlySubmission || !draftQueueKey) {
      return;
    }

    if (autosaveRef.current) {
      window.clearTimeout(autosaveRef.current);
    }

    autosaveRef.current = window.setTimeout(() => {
      const draftPayload: AbschlussberichtDraft = {
        projectId,
        sourceDocumentId: template.sourceDocumentId,
        sourceDocumentNumber: template.sourceDocumentNumber,
        templateKey: template.templateKey,
        templateVersion: template.templateVersion,
        sourceSignature: buildSourceSignature(template),
        checklistStates: SHOW_POSITION_CHECKLIST
          ? normalizeChecklistStates(checklistStates)
          : {},
        currentStepIndex,
        updatedAt: new Date().toISOString(),
        answers: steps.reduce<AbschlussberichtAnswer[]>((collected, step) => {
          const answer = answers[step.id];
          if (!answer) {
            return collected;
          }

          const isWarrantyStep = step.id === warrantyPaymentStepId;
          collected.push({
            stepId: step.id,
            itemNumber: step.itemNumber,
            displayTitle: step.displayTitle,
            planned: answer.planned,
            comment: answer.comment?.trim() || undefined,
            warrantyPaymentChoice: isWarrantyStep
              ? answer.warrantyPaymentChoice
              : undefined,
          });
          return collected;
        }, []),
        summaryQuantities,
      };

      const persistDraft = async () => {
        try {
          if (navigator.onLine) {
            await upsertAbschlussberichtDraft(projectId, draftPayload);
          } else {
            await addToQueue("abschlussbericht_draft_upsert", {
              queueKey: draftQueueKey,
              projectId,
              draft: draftPayload,
            });
          }
        } catch {
          await addToQueue("abschlussbericht_draft_upsert", {
            queueKey: draftQueueKey,
            projectId,
            draft: draftPayload,
          });
        }
      };

      void persistDraft();
    }, 750);

    return () => {
      if (autosaveRef.current) {
        window.clearTimeout(autosaveRef.current);
      }
    };
  }, [
    addToQueue,
    answers,
    checklistStates,
    currentStepIndex,
    draftHydrated,
    draftQueueKey,
    projectId,
    readOnlySubmission,
    steps,
    summaryQuantities,
    template,
    warrantyPaymentStepId,
  ]);

  const phaseStatus = getPhaseStatus(phaseId);
  const isCompleted = phaseStatus?.status === "completed";
  const isQueued = !!queueSubmission && !isCompleted;
  const isStepAnswerComplete = (
    step: AbschlussberichtTemplate["steps"][number],
    answer?: AbschlussberichtAnswer
  ): boolean => {
    if (!answer) {
      return false;
    }

    const hasCompletionAnswer = answer.planned || !!answer.comment?.trim();
    const hasWarrantyPaymentChoice =
      step.id !== warrantyPaymentStepId || !!answer.warrantyPaymentChoice;

    return hasCompletionAnswer && hasWarrantyPaymentChoice;
  };
  const isCurrentStepValid = currentStep
    ? isStepAnswerComplete(currentStep, currentAnswer || undefined)
    : false;
  const canSubmit =
    !!template &&
    steps.length > 0 &&
    hasValidSummaryQuantities &&
    confirmationAccepted;

  const updateAnswer = (stepId: string, partial: Partial<AbschlussberichtAnswer>) => {
    const step = steps.find((entry) => entry.id === stepId);
    if (!step) {
      return;
    }

    setAnswers((previous) => {
      const existing = previous[stepId] || {
        stepId,
        itemNumber: step.itemNumber,
        displayTitle: step.displayTitle,
        planned: partial.planned ?? false,
      };

      return {
        ...previous,
        [stepId]: {
          ...existing,
          ...partial,
        },
      };
    });
  };

  const updateChecklistItem = (stepId: string, index: number, checked: boolean) => {
    setChecklistStates((previous) => {
      const nextStepState = [...(previous[stepId] || [])];
      for (let cursor = 0; cursor < index; cursor += 1) {
        if (typeof nextStepState[cursor] !== "boolean") {
          nextStepState[cursor] = false;
        }
      }
      nextStepState[index] = checked;

      return {
        ...previous,
        [stepId]: nextStepState,
      };
    });
  };

  const toggleDescriptionVisibility = (stepId: string) => {
    setDescriptionVisibility((previous) => ({
      ...previous,
      [stepId]: !previous[stepId],
    }));
  };

  const updateQuantityInput = (field: QuantityFieldKey, value: string) => {
    if (value !== "" && !/^\d+$/.test(value)) {
      return;
    }

    setQuantityInputs((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const incrementQuantity = (field: QuantityFieldKey, delta: number) => {
    setQuantityInputs((previous) => {
      const currentValue = getQuantityValue(previous[field]);
      const nextValue = Math.max(0, currentValue + delta);
      return {
        ...previous,
        [field]: String(nextValue),
      };
    });
  };

  const handleSubmit = async () => {
    if (!template || !canSubmit || submitting) {
      return;
    }

    const submittedByEmail = user?.email?.trim();
    if (!submittedByEmail) {
      showToast("error", "Bitte erneut anmelden, um den Abschlussbericht abzusenden");
      return;
    }

    if (
      summaryQuantities.ventileAnzahl === 0 &&
      !window.confirm("Achtung, prüfe ob du wirklich 0 Ventile verbaut hast.")
    ) {
      return;
    }

    const submission: AbschlussberichtSubmission = {
      projectId,
      sourceDocumentId: template.sourceDocumentId,
      sourceDocumentNumber: template.sourceDocumentNumber,
      templateKey: template.templateKey,
      templateVersion: template.templateVersion,
      submittedAt: new Date().toISOString(),
      submittedByEmail,
      answers: steps.map((step) => {
        const isWarrantyStep = step.id === warrantyPaymentStepId;
        return {
          stepId: step.id,
          itemNumber: step.itemNumber,
          displayTitle: step.displayTitle,
          planned: !!answers[step.id]?.planned,
          comment: answers[step.id]?.comment?.trim() || undefined,
          warrantyPaymentChoice: isWarrantyStep
            ? answers[step.id]?.warrantyPaymentChoice
            : undefined,
        };
      }),
      summaryQuantities,
    };

    setSubmitting(true);
    if (draftStorageKey) {
      window.localStorage.removeItem(draftStorageKey);
    }

    setLocalSubmission(submission);
    updatePhaseStatus(phaseId, "completed", submission.submittedAt, submission.submittedByEmail);
    onCompleted?.(submission.submittedAt);
    setSubmitting(false);

    if (navigator.onLine) {
      showToast("success", "Abschlussbericht gespeichert", 3000);
    } else {
      showToast("info", "Abschlussbericht wurde offline in die Queue gelegt", 4000);
    }

    void Promise.resolve(
      addToQueue("abschlussbericht_submit", {
        projectId,
        phaseId,
        submission,
      })
    ).catch((submitError: unknown) => {
      setLocalSubmission(null);
      updatePhaseStatus(phaseId, "pending");
      const message =
        submitError instanceof Error
          ? submitError.message
          : "Abschlussbericht konnte nicht gespeichert werden";
      showToast("error", message, 4000);
    });
  };

  const handleRestartForEditing = () => {
    if (!readOnlySubmission) {
      return;
    }

    const confirmed = window.confirm(
      "Achtung: Der Abschlussbericht sollte nur in Ausnahmefällen neu gestartet werden. Möchtest du wirklich fortfahren?"
    );
    if (!confirmed) {
      return;
    }

    setAnswers(toAnswerMap(readOnlySubmission.answers));
    setQuantityInputs(normalizeQuantityInputs(readOnlySubmission.summaryQuantities));
    setChecklistStates({});
    setDescriptionVisibility({});
    setCurrentStepIndex(summaryStepIndex);
    setConfirmationAccepted(true);
    setLocalSubmission(null);
    setIsRestartedForEditing(true);
    updatePhaseStatus(phaseId, "pending");
    showToast("info", "Abschlussbericht kann wieder bearbeitet werden", 3000);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
          <p className="mt-5 text-base font-semibold text-gray-900 dark:text-white">
            Auftragsbestätigung wird analysiert
          </p>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Die KI strukturiert den Abschlussbericht. Das dauert in der Regel etwa 10 Sekunden.
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Bitte kurz warten.</p>
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
        {error || "Abschlussbericht konnte nicht geladen werden"}
      </div>
    );
  }

  if (readOnlySubmission) {
    const submittedByEmail = readOnlySubmission.submittedByEmail?.trim();
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b dark:border-gray-700">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Baustellen-Abschlussbericht
            </h2>
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                isCompleted
                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                  : "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
              }`}
            >
              {isCompleted ? "Abgeschickt" : "In Queue"}
            </span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Quelle: {template.sourceDocumentNumber}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Übermittelt am {new Date(readOnlySubmission.submittedAt).toLocaleString("de-DE")}
          </p>
          {submittedByEmail && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Abgeschlossen von {submittedByEmail}
            </p>
          )}
          <div className="mt-3">
            <button
              type="button"
              onClick={handleRestartForEditing}
              className="rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
            >
              Zur Bearbeitung zurück
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {readOnlySubmission.answers.map((answer) => {
            const submittedStep = steps.find((step) => step.id === answer.stepId);
            const warrantyPaymentChoiceLabel = getWarrantyPaymentChoiceLabel(
              answer.warrantyPaymentChoice
            );

            return (
              <div
                key={answer.stepId}
                className="rounded-lg border border-gray-200 dark:border-gray-700 p-3"
              >
                <div className="flex justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {answer.itemNumber}
                    </p>
                    {submittedStep?.quantityLabel && (
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {submittedStep.quantityLabel}
                      </p>
                    )}
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {answer.displayTitle}
                    </h3>
                  </div>
                  <span
                    className={`inline-flex h-8 min-w-[3.5rem] items-center justify-center self-start rounded-full px-3 text-sm font-semibold ${
                      answer.planned
                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                    }`}
                  >
                    {answer.planned ? "Ja" : "Nein"}
                  </span>
                </div>
                {warrantyPaymentChoiceLabel && (
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                    Zahlweise: {warrantyPaymentChoiceLabel}
                  </p>
                )}
                {!answer.planned && answer.comment && (
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {answer.comment}
                  </p>
                )}
              </div>
            );
          })}

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <p className="text-sm font-medium text-gray-900 dark:text-white">Mengenangaben</p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Anzahl Ventile
                </p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {readOnlySubmission.summaryQuantities.ventileAnzahl}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Rohre innen
                </p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {readOnlySubmission.summaryQuantities.rohreInnen}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Unterirdisch verlegte Rohre außen
                </p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {readOnlySubmission.summaryQuantities.rohreAussen}
                </p>
              </div>
            </div>
          </div>
        </div>

        {isQueued && (
          <div className="px-4 pb-4">
            <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 p-3 text-sm">
              Der Abschlussbericht wartet auf den nächsten erfolgreichen Sync.
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      <div className="p-4 border-b dark:border-gray-700">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Baustellen-Abschlussbericht
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Quelle: {template.sourceDocumentNumber}
            </p>
          </div>
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {isSummaryStep
              ? "Zusammenfassung"
              : isQuantitiesStep
                ? `Schritt ${quantitiesStepIndex + 1}/${summaryStepIndex + 1}`
                : `Schritt ${currentStepIndex + 1}/${summaryStepIndex + 1}`}
          </span>
        </div>
      </div>

      {!isQuantitiesStep && !isSummaryStep && currentStep ? (
        <div className="p-4">
          <div className="mb-4 min-h-[8.25rem]">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              {currentStep.itemNumber}
            </p>
            {currentStep.quantityLabel && (
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                {currentStep.quantityLabel}
              </p>
            )}
            <h3 className="min-h-[5.25rem] text-xl font-semibold leading-7 text-gray-900 dark:text-white">
              {currentStep.displayTitle}
            </h3>
            {currentStep.groupTitle && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {currentStep.groupTitle}
              </p>
            )}
          </div>

          {currentStep.descriptionHtml && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {!isProjectCompletionStep(currentStep.id) && (
                <button
                  type="button"
                  onClick={() => toggleDescriptionVisibility(currentStep.id)}
                  className="flex w-full items-center justify-between gap-3 bg-gray-50 dark:bg-gray-900/40 px-4 py-3 text-left"
                >
                  <span>
                    <span className="block text-sm font-medium text-gray-900 dark:text-white">
                      Beschreibung
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {isCurrentDescriptionExpanded ? "Ausblenden" : "Bei Bedarf aufklappen"}
                    </span>
                  </span>
                  <span
                    className={`text-gray-500 dark:text-gray-400 transition-transform ${
                      isCurrentDescriptionExpanded ? "rotate-180" : ""
                    }`}
                    aria-hidden="true"
                  >
                    ▼
                  </span>
                </button>
              )}

              {isCurrentDescriptionExpanded && (
                <div
                  className={`${isProjectCompletionStep(currentStep.id) ? "" : "border-t border-gray-200 dark:border-gray-700 "}bg-gray-50 dark:bg-gray-900/40 p-4 text-sm text-gray-700 dark:text-gray-300 [&_p]:mb-3 last:[&_p]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:ml-4 [&_ul]:mb-4 [&_ul]:mt-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:ml-4 [&_ol]:mb-4 [&_ol]:mt-2 [&_li]:mb-1 [&_ul+p]:mt-4 [&_ol+p]:mt-4`}
                  dangerouslySetInnerHTML={{ __html: currentStep.descriptionHtml }}
                />
              )}
            </div>
          )}

          {SHOW_POSITION_CHECKLIST && currentChecklistItems.length > 0 && (
            <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Positionen abhaken
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Wenn alle Positionen erledigt sind, wird &quot;Ja&quot; automatisch gesetzt.
                </p>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {currentChecklistItems.map((item, index) => (
                  <label
                    key={`${currentStep.id}-${index}`}
                    className="flex items-start gap-3 px-4 py-3 cursor-pointer"
                  >
                    <span className="min-w-0 flex-1 text-sm text-gray-700 dark:text-gray-300">
                      {item}
                    </span>
                    <input
                      type="checkbox"
                      checked={!!currentChecklistState[index]}
                      onChange={(event) =>
                        updateChecklistItem(currentStep.id, index, event.target.checked)
                      }
                      disabled={disabled}
                      className="mt-0.5 h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5">
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              {isProjectCompletionStep(currentStep.id)
                ? "Ist das Projekt vollständig abgeschlossen?"
                : "Lief wie geplant?"}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => updateAnswer(currentStep.id, { planned: true, comment: undefined })}
                disabled={disabled}
                className={`min-h-[48px] rounded-lg border px-4 py-3 text-sm font-medium ${
                  currentAnswer?.planned
                    ? "border-green-500 bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-200"
                    : "border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300"
                }`}
              >Ja</button>
              <button
                type="button"
                onClick={() => updateAnswer(currentStep.id, { planned: false })}
                disabled={disabled}
                className={`min-h-[48px] rounded-lg border px-4 py-3 text-sm font-medium ${
                  currentAnswer?.planned === false
                    ? "border-red-500 bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200"
                    : "border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300"
                }`}
              >Nein</button>
            </div>
          </div>

          {currentStepRequiresWarrantyPaymentChoice && (
            <fieldset className="mt-5 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <legend className="px-1 text-sm font-medium text-gray-900 dark:text-white">
                Zahlweise der Garantie
              </legend>
              <div className="mt-3 space-y-3">
                {WARRANTY_PAYMENT_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex cursor-pointer items-center gap-3 text-sm text-gray-700 dark:text-gray-300"
                  >
                    <input
                      type="radio"
                      name={`abschlussbericht-warranty-payment-${currentStep.id}`}
                      value={option.value}
                      checked={currentAnswer?.warrantyPaymentChoice === option.value}
                      onChange={() =>
                        updateAnswer(currentStep.id, {
                          warrantyPaymentChoice: option.value,
                        })
                      }
                      disabled={disabled}
                      className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {currentAnswer?.planned === false && (
            <div className="mt-4">
              <label
                htmlFor={`abschlussbericht-comment-${currentStep.id}`}
                className="block text-sm font-medium text-gray-900 dark:text-white mb-2"
              >
                Kommentar
              </label>
              <textarea
                id={`abschlussbericht-comment-${currentStep.id}`}
                value={currentAnswer.comment || ""}
                onChange={(event) => updateAnswer(currentStep.id, { comment: event.target.value })}
                disabled={disabled}
                rows={4}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                placeholder="Bitte die Abweichung kurz beschreiben"
              />
            </div>
          )}
        </div>
      ) : isQuantitiesStep ? (
        <div className="p-4 space-y-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Mengenangaben</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Bitte die tatsächlich verbauten Mengen eintragen. Alle drei Felder sind Pflicht.
            </p>
            <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100">
              ⚠️ Bei den Rohrlängen innen und außen gilt: Es ist die gesamte verbaute
              Rohrleitungsmenge zu erfassen. Die Strecke zwischen Innengerät und
              Wanddurchführung wird dabei mit Faktor 2 berücksichtigt.
            </div>
          </div>

          {[
            { field: "ventileAnzahl" as const, label: "Anzahl Ventile" },
            {
              field: "rohreInnen" as const,
              label: "Rohre innen",
            },
            {
              field: "rohreAussen" as const,
              label: "Unterirdisch verlegte Rohre außen",
            },
          ].map(({ field, label }) => (
            <div
              key={field}
              className="rounded-lg border border-gray-200 dark:border-gray-700 p-4"
            >
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                {label}
              </label>
              <div className="flex items-stretch overflow-hidden rounded-lg border border-gray-300 dark:border-gray-700 max-w-sm">
                <button
                  type="button"
                  onClick={() => incrementQuantity(field, -1)}
                  disabled={disabled}
                  className="w-14 bg-gray-100 dark:bg-gray-900 text-2xl font-semibold text-gray-800 dark:text-gray-200"
                >
                  -
                </button>
                <input
                  type="text"
                  inputMode="numeric"
                  value={quantityInputs[field]}
                  onChange={(event) => updateQuantityInput(field, event.target.value)}
                  disabled={disabled}
                  className="w-full min-w-0 border-x border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 text-center text-lg font-semibold text-gray-900 dark:text-white"
                  placeholder="0 erlaubt"
                />
                <button
                  type="button"
                  onClick={() => incrementQuantity(field, 1)}
                  disabled={disabled}
                  className="w-14 bg-green-500 text-2xl font-semibold text-white"
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-4 space-y-3">
          {steps.map((step) => {
            const answer = answers[step.id];
            const warrantyPaymentChoiceLabel = getWarrantyPaymentChoiceLabel(
              answer?.warrantyPaymentChoice
            );
            return (
              <div
                key={step.id}
                className="rounded-lg border border-gray-200 dark:border-gray-700 p-3"
              >
                <div className="flex justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {step.itemNumber}
                    </p>
                    {step.quantityLabel && (
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {step.quantityLabel}
                      </p>
                    )}
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {step.displayTitle}
                    </h3>
                  </div>
                  <span
                    className={`inline-flex h-8 min-w-[3.5rem] items-center justify-center self-start rounded-full px-3 text-sm font-semibold ${
                      answer?.planned
                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                    }`}
                  >
                    {answer?.planned ? "Ja" : "Nein"}
                  </span>
                </div>
                {answer?.planned === false && answer.comment && (
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {answer.comment}
                  </p>
                )}
                {warrantyPaymentChoiceLabel && (
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                    Zahlweise: {warrantyPaymentChoiceLabel}
                  </p>
                )}
              </div>
            );
          })}

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <p className="text-sm font-medium text-gray-900 dark:text-white">Mengenangaben</p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Anzahl Ventile
                </p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {summaryQuantities.ventileAnzahl}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Rohre innen
                </p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {summaryQuantities.rohreInnen}
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Unterirdisch verlegte Rohre außen
                </p>
                <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {summaryQuantities.rohreAussen}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <p className="font-medium mb-2">Finale Bestätigung</p>
            <p>
              Ich verstehe, wie wichtig dieser Bericht ist. Änderungen am Projekt im Vergleich zum
              Angebot sind in Ordnung, aber wir müssen darüber informiert werden. Wenn sich im
              Nachhinein herausstellt, dass wesentliche Abweichungen nicht angegeben wurden, kann
              sich das negativ auf meinen Baustellenbonus auswirken.
            </p>

            <label className="mt-4 flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmationAccepted}
                onChange={(event) => setConfirmationAccepted(event.target.checked)}
                disabled={disabled || submitting}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-amber-950">
                Ich bestätige, zur Kenntnis genommen.
              </span>
            </label>
          </div>
        </div>
      )}

      <div className="p-4 border-t dark:border-gray-700 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setCurrentStepIndex((previous) => Math.max(0, previous - 1))}
          disabled={currentStepIndex === 0 || disabled || submitting}
          className="min-h-[48px] rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50"
        >
          Zurück
        </button>

        {!isSummaryStep ? (
          <button
            type="button"
            onClick={() =>
              setCurrentStepIndex((previous) => Math.min(summaryStepIndex, previous + 1))
            }
            disabled={
              disabled ||
              (!isQuantitiesStep && !isCurrentStepValid) ||
              (isQuantitiesStep && !hasValidSummaryQuantities)
            }
            className="min-h-[48px] rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {currentStepIndex === steps.length - 1
              ? "Zu den Mengen"
              : isQuantitiesStep
                ? "Zur Zusammenfassung"
                : "Weiter"}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || disabled || submitting}
            className="min-h-[48px] rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {submitting ? "Wird gesendet..." : "Absenden"}
          </button>
        )}
      </div>
    </div>
  );
};

export default AbschlussberichtWizard;
